import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { toast } from "sonner";
import { Loader2, Download, Tag, Users, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (v, d = 0) => `€${Number(v).toLocaleString("es-ES", { minimumFractionDigits: d, maximumFractionDigits: d })}`;

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border transition-colors ${
      active ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"
    }`}
  >
    {children}
  </button>
);

const PromoTable = ({ data, promoKey }) => {
  const promo = data.promos[promoKey];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs sm:text-sm" data-testid="promo-table">
        <thead>
          <tr className="border-b text-xs uppercase text-muted-foreground">
            <th className="text-left py-2 px-2">Habitación</th>
            <th className="text-right py-2 px-2">Precio temporada</th>
            <th className="text-right py-2 px-2 text-primary">Publicar /noche</th>
            <th className="text-right py-2 px-2">Cliente paga ({promo.stay} noches)</th>
            <th className="text-right py-2 px-2">ADR real</th>
          </tr>
        </thead>
        <tbody>
          {data.rooms.map((r) => {
            const p = r.promos[promoKey];
            return (
              <tr key={r.name} className="border-b hover:bg-muted/30" data-testid={`promo-row-${r.name}`}>
                <td className="py-2 px-2 font-medium">
                  {r.name} <span className="text-xs text-muted-foreground">×{r.qty}</span>
                </td>
                <td className="py-2 px-2 text-right text-muted-foreground">{eur(r.season_price)}</td>
                <td className="py-2 px-2 text-right font-bold text-primary">{eur(p.published)}</td>
                <td className="py-2 px-2 text-right font-semibold">{eur(p.client_pays)}</td>
                <td className="py-2 px-2 text-right">{eur(p.effective_adr, 1)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const BreakEven = ({ occ, discount }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
    <div className="bg-muted/30 rounded-lg p-3">
      <p className="text-xs text-muted-foreground">Ocupación {occ.last_year_label}</p>
      <p className="font-bold text-lg" data-testid="promo-occ-last-year">{occ.last_year != null ? `${occ.last_year}%` : "—"}</p>
    </div>
    <div className="bg-muted/30 rounded-lg p-3">
      <p className="text-xs text-muted-foreground">Ocupación necesaria (×{occ.break_even_multiplier})</p>
      <p className={`font-bold text-lg ${occ.reachable ? "text-green-700" : "text-red-600"}`} data-testid="promo-break-even">
        {occ.break_even_occupancy != null ? `${Math.min(occ.break_even_occupancy, 100)}%` : "—"}
        {occ.break_even_occupancy > 100 ? " · no alcanzable" : ""}
      </p>
    </div>
    <div className="bg-muted/30 rounded-lg p-3">
      <p className="text-xs text-muted-foreground">Ya reservado (OTB)</p>
      <p className="font-bold text-lg" data-testid="promo-occ-otb">{occ.current_otb != null ? `${occ.current_otb}%` : "—"}</p>
    </div>
    <p className="sm:col-span-3 text-xs text-muted-foreground flex items-start gap-1">
      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
      Con un descuento real del {discount}% cada noche ingresa un {100 - discount}% del precio de temporada: para igualar los ingresos del año pasado hace falta ×{occ.break_even_multiplier} de ocupación.
    </p>
  </div>
);

export const PromoCalculator = () => {
  const [month, setMonth] = useState(11);
  const [dayType, setDayType] = useState("lj");
  const [promoKey, setPromoKey] = useState("2x1");
  const [discount, setDiscount] = useState([30]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }), []);
  const params = `month=${month}&day_type=${dayType}&discount=${discount[0]}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/promos/calculator?${params}`, { headers: headers() })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => toast.error("Error cargando la calculadora de promos"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params, headers]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/promos/calculator/pdf?${params}`, { headers: headers(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `promos_${data?.month_label?.replace(" ", "_") || month}_${dayType}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error("Error al descargar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  const months = data?.season_months || [];
  const promos = data?.promos || {};

  return (
    <Card data-testid="promo-calculator">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" /> Calculadora Promo 2x1 · temporada baja
            </CardTitle>
            <CardDescription>
              Sin demanda no subimos precios: qué precio publicar para que la promo deje el descuento real que tú decidas
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading || !data} data-testid="promo-download-pdf-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Descargar PDF
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-2" data-testid="promo-month-chips">
          {months.map((m) => (
            <Chip key={m.label} active={m.month === month} onClick={() => setMonth(m.month)} testId={`promo-month-${m.month}`}>{m.label}</Chip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Chip active={dayType === "lj"} onClick={() => setDayType("lj")} testId="promo-day-lj">Lunes–Jueves</Chip>
          <Chip active={dayType === "vs"} onClick={() => setDayType("vs")} testId="promo-day-vs">Viernes–Sábado</Chip>
          <span className="w-px h-5 bg-border mx-1 hidden sm:block" />
          {Object.entries(promos).map(([k, p]) => (
            <Chip key={k} active={promoKey === k} onClick={() => setPromoKey(k)} testId={`promo-type-${k}`}>{p.label}</Chip>
          ))}
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Descuento real que asumes</span>
            <span className="font-bold" data-testid="promo-discount-value">{discount[0]}%</span>
          </div>
          <Slider value={discount} onValueChange={setDiscount} min={10} max={50} step={5} data-testid="promo-discount-slider" />
          <p className="text-xs text-muted-foreground mt-2">
            {promos[promoKey]?.description || ""}. Subes el precio publicado y regalas noches: el cliente ve la promo, tú controlas el ADR.
          </p>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Calculando…</div>
        ) : data ? (
          <>
            {data.reference_note && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="promo-reference-note">{data.reference_note}</p>
            )}
            <PromoTable data={data} promoKey={promoKey} />
            <div className="flex items-center gap-2 text-sm font-semibold"><Users className="w-4 h-4" /> ¿Compensa? Punto de equilibrio {data.month_label}</div>
            <BreakEven occ={data.occupancy} discount={discount[0]} />
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default PromoCalculator;
